import { useState } from 'react';
import { IconCheck, IconPhoto } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  CONTACTS, DOCS, GVT_DOC, MEMOS0, PHASES, PHOTOS0, TRIP, navInfo, photoNote, removeSelected, selCount,
  type Memo, type Photo,
} from './navData';
import './nav.i18n';
import s from './nav.module.css';

const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
function forcedState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

type Sheet = 'memo' | 'gvt' | 'move' | null;

/** Mint-Mov-Phone3 khung 3 "Dẫn đường & chứng từ" (giả lập, giữ trong bộ nhớ, toast có Hoàn tác). Bản đồ: khung tĩnh theo board, chưa nối VietMap. */
export function Nav() {
  const t = useT();
  const state = forcedState();
  const toast = useToast();
  const [voice, setVoice] = useState(true);
  const [off, setOff] = useState(false);
  const [sel, setSel] = useState<Record<number, boolean>>({});
  const [photos, setPhotos] = useState<readonly Photo[]>(PHOTOS0);
  const [memos, setMemos] = useState<readonly Memo[]>(MEMOS0);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('');
  const [fErr, setFErr] = useState(false);
  const n = selCount(sel);
  const nav = navInfo(off);
  const openSheet = (w: Sheet) => { setText(''); setPhase(''); setFErr(false); setSheet(w); };

  const row = (k: string, v: string, tone: 'ok' | 'err' | 'pri' | undefined, act?: { label: string; on: () => void; pri?: boolean }) => (
    <div key={k} className={s.row}>
      <span className={s.dot} data-t={tone} aria-hidden="true" />
      <div><div className={s.k}>{k}</div><div className={s.v}>{v}</div></div>
      {act && <button type="button" className={s.act} data-k={act.pri ? 'pri' : undefined} onClick={act.on}>{act.label}</button>}
    </div>
  );
  const saveMemo = () => {
    if (!text.trim()) { setFErr(true); return; }
    const prev = memos;
    setMemos([...memos, [t('mvg.me'), text, t('mvg.now')]]);
    setSheet(null);
    toast({ msg: t('mvg.memoAdded'), undo: () => setMemos(prev) });
  };
  const movePhotos = () => {
    if (!phase) { setFErr(true); return; }
    setSheet(null); setSel({});
    toast({ msg: t('mvg.moved', { n, p: phase }) });
  };
  const removePhotos = () => {
    const prev = { photos, sel };
    setPhotos(removeSelected(photos, sel)); setSel({});
    toast({ msg: t('mvg.removed', { n }), undo: () => { setPhotos(prev.photos); setSel(prev.sel); } });
  };

  return (
    <>
      <PageHeader title={t('mvg.title')} />
      <div className={s.page}>
        {state === 'error' && <div role="status" className={s.net}><b>{t('mvg.offB')}</b> {t('mvg.offT')}</div>}
        {state === 'noperm' && <div role="status" className={s.net}><b>{t('mvg.npB')}</b> {t('mvg.npT')}</div>}
        <StateView
          state={state === 'error' || state === 'noperm' || state === 'empty' ? 'ready' : state}
          skeleton={<>{[0, 1, 2].map((i) => <div key={i} className={s.sk} aria-hidden="true" />)}</>}
        >
          {state === 'empty' ? (
            <section className={s.card} aria-label={t('mvg.none')}>
              <div className={s.hd}><b>{t('mvg.none')}</b></div>
              {row(t('mvg.navK'), t('mvg.navV'), undefined)}
            </section>
          ) : state === 'noperm' ? null : (
            <>
              <section className={s.card} aria-label={t('mvg.navTo', { d: TRIP.dest })}>
                <div className={s.hd}><b>{t('mvg.navTo', { d: TRIP.dest })}</b><span data-t={off ? 'err' : undefined}>{nav.note}</span></div>
                <div className={s.map} role="img" aria-label={t('mvg.map')}>
                  <svg aria-hidden="true" viewBox="0 0 360 230" preserveAspectRatio="none">
                    <path d="M0 60 C 90 70, 160 120, 360 100" fill="none" stroke="var(--bd)" strokeWidth="10" />
                    <path d="M180 230 C 170 160, 200 110, 260 0" fill="none" stroke="var(--bd)" strokeWidth="8" />
                    <path d={nav.route} fill="none" stroke="var(--pri)" strokeWidth="6" strokeLinecap="round" />
                  </svg>
                  <span className={s.me} />
                  <span className={s.head}>B 32°</span>
                </div>
                <div className={s.turn} data-off={off ? '1' : '0'}><span aria-hidden="true">{nav.arrow}</span><div><b>{nav.dist}</b><div>{nav.turn}</div></div></div>
                {row(t('mvg.voice'), voice ? t('mvg.voiceOn') : t('mvg.voiceOff'), voice ? 'ok' : undefined, { label: voice ? t('mvg.turnOff') : t('mvg.turnOn'), on: () => setVoice(!voice) })}
                {row(t('mvg.reK'), off ? t('mvg.reOff') : t('mvg.reAuto'), off ? 'err' : undefined,
                  off ? { label: t('mvg.reDo'), pri: true, on: () => { setOff(false); toast({ msg: t('mvg.reDone') }); } } : { label: t('mvg.tryOff'), on: () => setOff(true) })}
                {row(t('mvg.extK'), t('mvg.extV'), undefined, { label: t('mvg.open'), on: () => toast({ msg: t('mvg.gmaps') }) })}
              </section>

              <section className={s.card} aria-label={t('mvg.contacts')}>
                <b className={s.hd}>{t('mvg.contacts')}</b>
                {CONTACTS.map((c) => row(c.k, c.v, undefined, { label: t('mvg.call'), pri: true, on: () => toast({ msg: t('mvg.calling', { w: c.who }) }) }))}
              </section>

              <section className={s.card} aria-label={t('mvg.memos')}>
                <div className={s.hd}><b>{t('mvg.memos')}</b><span>{t('mvg.memoN', { n: memos.length })}</span></div>
                {memos.map(([w, k, tm]) => row(k, `${w} · ${tm}`, 'pri'))}
                <button type="button" className={s.soft} onClick={() => openSheet('memo')}>{t('mvg.memoAdd')}</button>
              </section>

              <section className={s.card} aria-label={t('mvg.docs')}>
                <b className={s.hd}>{t('mvg.docs')}</b>
                {DOCS.map((d) => row(d.k, d.v, 'ok', { label: t('mvg.dl'), on: () => toast({ msg: d.msg }) }))}
                <button type="button" className={s.btn} onClick={() => openSheet('gvt')}>{t('mvg.gvtBtn')}</button>
              </section>

              <section className={s.card} aria-label={t('mvg.photos')}>
                <div className={s.hd}><b>{t('mvg.photos')}</b><span >{photoNote(photos.length, n)}</span></div>
                <div className={s.grid}>
                  {photos.map(([k], i) => (
                    <button key={`${k}-${i}`} type="button" className={s.cell} data-on={sel[i] ? '1' : '0'} aria-pressed={!!sel[i]} aria-label={k}
                      onClick={() => setSel({ ...sel, [i]: !sel[i] })}>
                      {sel[i] ? <IconCheck size={20} stroke={1.8} aria-hidden="true" /> : <IconPhoto size={20} stroke={1.8} aria-hidden="true" />}{k}
                    </button>
                  ))}
                </div>
                {n > 0 && <button type="button" className={s.soft} onClick={() => openSheet('move')}>{t('mvg.moveBtn', { n })}</button>}
                {n > 0 && <button type="button" className={s.plain} onClick={removePhotos}>{t('mvg.removeBtn', { n })}</button>}
              </section>
            </>
          )}
        </StateView>
      </div>

      <Modal open={sheet === 'memo'} onOpenChange={(o) => !o && setSheet(null)} title={t('mvg.memoAdd')}
        footer={<button type="button" className={s.btn} onClick={saveMemo}>{t('mvg.save')}</button>}>
        <p className={s.hint}>{t('mvg.memoHint')}</p>
        <label className={s.field}><span>{t('mvg.memo')}</span><input placeholder={t('mvg.memoPh')} value={text} onChange={(e) => setText(e.target.value)} />{fErr && !text.trim() && <span className={s.err}>{t('mvg.memoErr')}</span>}</label>
      </Modal>

      <Modal open={sheet === 'gvt'} onOpenChange={(o) => !o && setSheet(null)} title={t('mvg.gvtTitle')}
        footer={<button type="button" className={s.btn} onClick={() => { setSheet(null); toast({ msg: t('mvg.gvtDone') }); }}>{t('mvg.share')}</button>}>
        <p className={s.hint}>{t('mvg.gvtHint')}</p>
        <pre className={s.doc}>{GVT_DOC}</pre>
      </Modal>

      <Modal open={sheet === 'move'} onOpenChange={(o) => !o && setSheet(null)} title={t('mvg.moveTitle')}
        footer={<button type="button" className={s.btn} onClick={movePhotos}>{t('mvg.move')}</button>}>
        <p className={s.hint}>{t('mvg.picked', { n })}</p>
        <label className={s.field}><span>{t('mvg.dest')}</span>
          <select value={phase} onChange={(e) => setPhase(e.target.value)}><option value="">—</option>{PHASES.map((p) => <option key={p}>{p}</option>)}</select>
          {fErr && !phase && <span className={s.err}>{t('mvg.phaseErr')}</span>}
        </label>
      </Modal>
    </>
  );
}
